// Global

import { useFacetConfig } from 'components/authorable/shared/card-listing/ContentGrid/FacetConfigContext';
import { SearchFacetGroup } from 'lib/services/content-service';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { SearchRequest } from 'src/pages/api/search/content-list';

const getCurrentHash = () =>
  typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, '');

const getCurrentHistoryIndex = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const historyState = window.history.state as { idx?: unknown } | null;
  return typeof historyState?.idx === 'number' ? historyState.idx : null;
};

// ---------------------------------------------------------------------------
// sessionStorage helpers — persist the facet hash across cross-page navigation
// so pressing browser back from a product/recipe page restores selected facets.
// ---------------------------------------------------------------------------
const FACET_RETURN_HASH_KEY = 'facet-return-hash';

type StoredFacetReturnHash = {
  hash: string;
  historyIndex: number | null;
};

const saveFacetReturnHash = (pathname: string, hash: string, historyIndex: number | null): void => {
  if (typeof window === 'undefined' || !hash) return;
  try {
    const payload: StoredFacetReturnHash = { hash, historyIndex };
    sessionStorage.setItem(`${FACET_RETURN_HASH_KEY}:${pathname}`, JSON.stringify(payload));
  } catch {
    // sessionStorage unavailable (private browsing, storage quota, etc.)
  }
};

const consumeFacetReturnHash = (pathname: string): string | null => {
  if (typeof window === 'undefined') return null;
  const key = `${FACET_RETURN_HASH_KEY}:${pathname}`;
  try {
    const saved = sessionStorage.getItem(key);
    if (!saved) {
      return null;
    }

    const currentHistoryIndex = getCurrentHistoryIndex();

    try {
      const parsed = JSON.parse(saved) as StoredFacetReturnHash;
      const shouldRestore =
        typeof parsed?.hash === 'string' &&
        parsed.hash.length > 0 &&
        parsed.historyIndex !== null &&
        currentHistoryIndex !== null &&
        currentHistoryIndex === parsed.historyIndex;

      sessionStorage.removeItem(key);
      return shouldRestore ? parsed.hash : null;
    } catch {
      // Backward-compatibility for older plain-string entries: consume and drop
      // them so stale facet hashes are not restored on fresh navigation.
      sessionStorage.removeItem(key);
      return null;
    }
  } catch {
    return null;
  }
};

const hasFacetHash = (hash: string) => {
  if (!hash) {
    return false;
  }

  const params = new URLSearchParams(hash ? `?${hash}` : '');
  for (const key of params.keys()) {
    if (key.startsWith(FACET_PREFIX)) {
      return true;
    }
  }

  return false;
};

const isFacetManagedHash = (hash: string) => {
  return hash === '' || hasFacetHash(hash);
};

const getInitialFacetRestoreState = () => {
  const currentHash = getCurrentHash();
  if (currentHash && hasFacetHash(currentHash)) {
    return {
      pendingUrlHash: currentHash,
      restoreHashWithReplaceState: false,
    };
  }

  if (typeof window === 'undefined') {
    return {
      pendingUrlHash: null,
      restoreHashWithReplaceState: false,
    };
  }

  const restoredHash = consumeFacetReturnHash(window.location.pathname);
  return {
    pendingUrlHash: restoredHash,
    restoreHashWithReplaceState: !!restoredHash,
  };
};

/**
 * Ensures that updating facets updates the url, and returns the selected facets from url
 * to pass to useSearchResults.
 * @returns The current selected facets from the url
 */
export const useFacetUrlSync = (query: SearchRequest | null) => {
  const { facetConfig, getFacetId } = useFacetConfig();
  const initialRestoreState = useMemo(getInitialFacetRestoreState, []);
  const [hash, setHash] = useState(getCurrentHash);
  const [restoreHashWithReplaceState, setRestoreHashWithReplaceState] = useState(
    initialRestoreState.restoreHashWithReplaceState
  );
  const [pendingUrlHash, setPendingUrlHash] = useState<string | null>(
    initialRestoreState.pendingUrlHash
  );
  const selectedFacetsFromUrl = useMemo(() => {
    if (pendingUrlHash === null) {
      return null;
    }

    return parseFacetGroupsForQuery(pendingUrlHash, facetConfig.facetGroup, getFacetId);
  }, [facetConfig.facetGroup, getFacetId, pendingUrlHash]);
  const facetUrlFragment = useMemo(() => facetToUrlFragment(query?.facetGroups ?? []), [query]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleHashChange = () => {
      const nextHash = getCurrentHash();
      setHash(nextHash);

      if (!nextHash) {
        setPendingUrlHash('');
        return;
      }

      if (!hasFacetHash(nextHash)) {
        return;
      }

      setPendingUrlHash(nextHash);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (pendingUrlHash === null) {
      return;
    }

    if (facetUrlFragment === pendingUrlHash) {
      setPendingUrlHash(null);
    }
  }, [facetUrlFragment, pendingUrlHash]);

  useEnsureFacetUrl(
    hash,
    facetUrlFragment,
    pendingUrlHash,
    restoreHashWithReplaceState,
    setHash,
    setRestoreHashWithReplaceState
  );
  return selectedFacetsFromUrl;
};

/**
 * Updates the url when selected facets change
 */
const useEnsureFacetUrl = (
  hash: string,
  facetUrlFragment: string,
  pendingUrlHash: string | null,
  restoreHashWithReplaceState: boolean,
  setHash: (hash: string) => void,
  setRestoreHashWithReplaceState: (shouldReplace: boolean) => void
) => {
  const router = useRouter();

  // Save the current facet hash to sessionStorage before any page navigation.
  // This lets useFacetUrlSync restore the facets when the user presses the
  // browser back button from a product/recipe/article page.
  useEffect(() => {
    const handleRouteChangeStart = () => {
      const currentHash = getCurrentHash();
      if (currentHash && hasFacetHash(currentHash)) {
        saveFacetReturnHash(window.location.pathname, currentHash, getCurrentHistoryIndex());
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => router.events.off('routeChangeStart', handleRouteChangeStart);
  }, [router.events]);

  useEffect(() => {
    if (!router.isReady || typeof window === 'undefined') {
      return;
    }

    // Let browser-driven back/forward hash changes hydrate query state first.
    if (pendingUrlHash !== null) {
      return;
    }

    // Only update when the canonical hash fragment actually changed.
    if (hash === facetUrlFragment) {
      return;
    }

    // Preserve unrelated in-page anchors. This hook owns only facet hashes,
    // so pages that deep-link to internal sections must keep their hash intact.
    if (!isFacetManagedHash(hash)) {
      return;
    }

    const newUrl = buildFacetSyncUrl({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: facetUrlFragment,
    });
    const shouldReplaceHistoryState = restoreHashWithReplaceState || facetUrlFragment === '';

    // If the current entry is a proper Next.js state (__N: true), push a new
    // entry that preserves the internal Next.js route state while changing only
    // the visible URL/hash. This keeps per-facet back-stack steps intact inside
    // the search page and still lets cross-page back navigation restore the
    // search term via the preserved `url` field.
    const currentState = window.history.state;
    if (currentState?.__N) {
      if (shouldReplaceHistoryState) {
        window.history.replaceState({ ...currentState, as: newUrl }, '', newUrl);
      } else {
        window.history.pushState({ ...currentState, as: newUrl }, '', newUrl);
      }
    } else {
      if (shouldReplaceHistoryState) {
        window.history.replaceState(currentState, '', newUrl);
      } else {
        window.history.pushState(null, '', newUrl);
      }
    }

    if (restoreHashWithReplaceState) {
      setRestoreHashWithReplaceState(false);
    }
    setHash(facetUrlFragment);
  }, [
    facetUrlFragment,
    hash,
    pendingUrlHash,
    restoreHashWithReplaceState,
    router,
    setHash,
    setRestoreHashWithReplaceState,
  ]);
};

// Optional prefix to indicate a hash parameter is a facet.  Recommended so as to not override other hash parameters
const FACET_PREFIX = 'f-';

export function facetToUrlFragment(selectedFacets: SearchFacetGroup[]): string {
  if (!selectedFacets.length) {
    return '';
  }
  const facets: Record<string, string> = {};
  selectedFacets.forEach((facet) => {
    // Depending on when it's called, sometimes it comes as facet.valueLabel, other times it's facet.facetValueText
    if (!facet.facets.length) {
      return;
    }
    // Get the key, e.g. a facet of "color" would return "f-color"
    const key = FACET_PREFIX + facet.name;
    facets[key] = facet.facets.map((x) => x.name).join('|');
  });
  const params = new URLSearchParams(facets);

  return params.toString();
}

export function buildFacetSyncUrl({
  pathname,
  search,
  hash,
}: {
  pathname: string;
  search: string;
  hash: string;
}) {
  return `${pathname}${search}${hash ? `#${hash}` : ''}`;
}

export function parseFacetGroupsFromHash(
  hash: string,
  getFacetId: (facetGroupName: string, facetValue: string) => string | undefined
): SearchFacetGroup[] {
  const hashAsQuery = new URLSearchParams(hash ? `?${hash}` : '');
  const facets: SearchFacetGroup[] = [];

  for (const [key, value] of hashAsQuery.entries()) {
    if (!key.startsWith(FACET_PREFIX)) {
      continue;
    }

    const facetName = key.split(FACET_PREFIX)[1];
    const valueArray = value.split('|');

    facets.push({
      name: facetName,
      facets: valueArray.map((facetValue) => ({
        name: facetValue,
        facetId: getFacetId(facetName, facetValue) ?? '',
      })),
    });
  }

  return facets;
}

function parseFacetGroupsForQuery(
  hash: string,
  configuredFacetGroups: { groupName: string }[],
  getFacetId: (facetGroupName: string, facetValue: string) => string | undefined
): SearchFacetGroup[] {
  const selectedFacetGroups = parseFacetGroupsFromHash(hash, getFacetId);

  if (selectedFacetGroups.length === 0) {
    return [];
  }

  const selectedFacetGroupsByName = new Map(
    selectedFacetGroups.map((group) => [group.name, group.facets] as const)
  );

  return configuredFacetGroups.map((group) => ({
    name: group.groupName,
    facets: selectedFacetGroupsByName.get(group.groupName) ?? [],
  }));
}
